import {useNavigate} from 'react-router-dom';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Badge} from '@common-web/components/ui/badge';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Activity,
  Layers,
  Zap,
} from 'lucide-react';
import {formatDistanceToNow, format} from 'date-fns';

type SyncRun = RouterOutput['internal']['vbaSyncRun']['list']['runs'][number];

type SyncRunFilters = {syncType: string; status: string};

export function VbaSyncRunsList() {
  const navigate = useNavigate();

  const table = useDataTable<SyncRunFilters>({
    defaultPageSize: 20,
    defaultFilters: {syncType: '', status: ''},
  });

  const {data: syncTypes} = trpc.internal.vbaSyncRun.getSyncTypes.useQuery();

  const {
    data: runsData,
    isLoading,
    isFetching,
    refetch,
  } = trpc.internal.vbaSyncRun.list.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    syncType: (table.filterValues.syncType || undefined) as
      | 'MEMBER'
      | 'GROUP'
      | 'FULL_SYNC'
      | 'PLAN_SYNC'
      | 'ENROLLMENT_SYNC'
      | undefined,
    status: (table.filterValues.status || undefined) as
      | 'PENDING'
      | 'RUNNING'
      | 'COMPLETED'
      | 'FAILED'
      | undefined,
  });

  const {data: stats, isLoading: statsLoading} = trpc.internal.vbaSyncRun.getStats.useQuery({
    days: 7,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <Badge className='bg-emerald text-white border-0 font-medium'>
            <CheckCircle2 className='h-3 w-3 mr-1' />
            Completed
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge className='bg-red-500 text-white border-0 font-medium'>
            <XCircle className='h-3 w-3 mr-1' />
            Failed
          </Badge>
        );
      case 'RUNNING':
        return (
          <Badge className='bg-brand-blue text-white border-0 font-medium'>
            <Loader2 className='h-3 w-3 mr-1 animate-spin' />
            Running
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge className='bg-brand-gray text-white border-0 font-medium'>
            <Clock className='h-3 w-3 mr-1' />
            Pending
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getSyncTypeBadge = (syncType: string) => {
    const colors: Record<string, string> = {
      MEMBER: 'bg-purple-100 text-purple-700 border-purple-200',
      GROUP: 'bg-blue-100 text-blue-700 border-blue-200',
      FULL_SYNC: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      PLAN_SYNC: 'bg-teal-100 text-teal-700 border-teal-200',
      ENROLLMENT_SYNC: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    return (
      <Badge className={`${colors[syncType] || 'bg-gray-100 text-gray-700'} font-medium text-xs`}>
        {syncType.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  const columns: Column<SyncRun>[] = [
    {
      id: 'syncType',
      header: 'Type',
      render: (run) => getSyncTypeBadge(run.syncType),
    },
    {
      id: 'status',
      header: 'Status',
      render: (run) => getStatusBadge(run.status),
    },
    {
      id: 'startedAt',
      header: 'Started',
      render: (run) => (
        <div className='text-sm'>
          <div className='text-brand-navy'>{format(new Date(run.startedAt), 'MMM d, h:mm a')}</div>
          <div className='text-brand-gray text-xs'>
            {formatDistanceToNow(new Date(run.startedAt), {addSuffix: true})}
          </div>
        </div>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      render: (run) => (
        <span className='text-sm font-mono text-brand-navy'>{formatDuration(run.durationMs)}</span>
      ),
    },
    {
      id: 'groups',
      header: 'Groups',
      render: (run) => (
        <div className='flex items-center gap-2 text-sm'>
          <span className='font-medium text-brand-navy'>{run.totalGroups}</span>
          {(run.groupsSucceeded ?? 0) > 0 && (
            <span className='text-emerald flex items-center gap-0.5'>
              <CheckCircle2 className='h-3 w-3' />
              {run.groupsSucceeded}
            </span>
          )}
          {(run.groupsFailed ?? 0) > 0 && (
            <span className='text-red-500 flex items-center gap-0.5'>
              <XCircle className='h-3 w-3' />
              {run.groupsFailed}
            </span>
          )}
          {(run.groupsRunning ?? 0) > 0 && (
            <span className='text-brand-blue flex items-center gap-0.5'>
              <Loader2 className='h-3 w-3 animate-spin' />
              {run.groupsRunning}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (run) => (
        <Button
          size='sm'
          variant='ghost'
          className='text-brand-blue hover:text-brand-blue-dark'
          onClick={() => navigate(`/vba-sync/${run.id}`)}
        >
          View
          <ChevronRight className='h-4 w-4 ml-1' />
        </Button>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='VBA Sync Runs'
          description='Detailed sync execution history with per-group breakdowns and logs'
          icon={<Activity className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Stats Cards */}
          <div className='grid grid-cols-1 md:grid-cols-6 gap-4 mb-6'>
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray'>Runs (7d)</CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>{stats?.totalRuns || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-emerald' />
                  Completed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-emerald'>{stats?.completedRuns || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <XCircle className='h-4 w-4 text-red-500' />
                  Failed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-red-500'>{stats?.failedRuns || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <Layers className='h-4 w-4 text-brand-blue' />
                  Group Syncs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-blue'>
                    {stats?.totalGroupSyncs || 0}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <AlertTriangle className='h-4 w-4 text-amber' />
                  Errors
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-amber'>{stats?.totalErrors || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <Zap className='h-4 w-4 text-brand-navy' />
                  Success Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>
                    {stats?.successRate || 0}%
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Runs Table */}
          <DataTable<SyncRun, SyncRunFilters>
            table={table}
            columns={columns}
            data={runsData?.runs}
            total={runsData?.totalCount}
            totalPages={runsData?.totalPages}
            loading={isLoading}
            emptyMessage='No sync runs found'
            title='Sync Runs'
            entity={{singular: 'run', plural: 'runs'}}
            filters={[
              {
                key: 'syncType',
                label: 'Types',
                type: 'select',
                placeholder: 'All Types',
                options: (syncTypes ?? []).map((type) => ({
                  value: type,
                  label: type.replace(/_/g, ' '),
                })),
              },
              {
                key: 'status',
                label: 'Status',
                type: 'select',
                placeholder: 'All Status',
                options: [
                  {value: 'COMPLETED', label: 'Completed'},
                  {value: 'FAILED', label: 'Failed'},
                  {value: 'RUNNING', label: 'Running'},
                  {value: 'PENDING', label: 'Pending'},
                ],
              },
            ]}
            rowKey={(run) => run.id}
          />
        </div>
      </div>
    </div>
  );
}
